import { useMemo } from "react";
import { MedspaDiscountsQuery } from "@/graphql/queries/settings/medspaDiscounts.graphql.types";
import { DiscountTypeValues } from "@/hooks/settings/discounts/useDiscountsCommon";
import { DiscountType } from "@/types";

export const formatDiscountValue = (discountType: string, value: string) => {
  return discountType === "percent" ? `${+value}%` : `$${value}`;
};

export const getHumanizedDiscountType = (discountType: DiscountType) => {
  if (discountType === DiscountType.ENTIRE_PURCHASE) return `entire purchase`;
  if (discountType === DiscountType.SERVICE) return `services menu items`;
  if (discountType === DiscountType.SERVICE_PRODUCT) return `service products`;
  if (discountType === DiscountType.RETAIL_PRODUCT) return `retail products`;
  return "";
};

type Discount = MedspaDiscountsQuery["medspaByPk"]["discounts"][number];

export const determineDiscountType = (discount: {
  discountServiceMenuItems: unknown[];
  discountServiceProducts: unknown[];
  discountRetailProducts: unknown[];
}): DiscountType => {
  if (discount.discountServiceMenuItems.length > 0) return DiscountType.SERVICE;
  if (discount.discountServiceProducts.length > 0) {
    return DiscountType.SERVICE_PRODUCT;
  }
  if (discount.discountRetailProducts.length > 0)
    return DiscountType.RETAIL_PRODUCT;

  return DiscountType.ENTIRE_PURCHASE;
};

export const getDiscountGroups = (
  discountsData: MedspaDiscountsQuery["medspaByPk"]["discounts"]
) => {
  const serviceMenuItemDiscounts: Discount[] = [];
  const serviceProductDiscounts: Discount[] = [];
  const retailProductDiscounts: Discount[] = [];
  const generalDiscounts: Discount[] = [];

  discountsData?.forEach((discount) => {
    if (discount.discountServiceMenuItems.length > 0) {
      serviceMenuItemDiscounts.push(discount);
      return;
    }
    if (discount.discountRetailProducts.length > 0) {
      retailProductDiscounts.push(discount);
      return;
    }
    if (discount.discountServiceProducts.length > 0) {
      serviceProductDiscounts.push(discount);
      return;
    }

    generalDiscounts.push(discount);
  });
  return {
    [DiscountType.ENTIRE_PURCHASE]: generalDiscounts,
    [DiscountType.SERVICE]: serviceMenuItemDiscounts,
    [DiscountType.SERVICE_PRODUCT]: serviceProductDiscounts,
    [DiscountType.RETAIL_PRODUCT]: retailProductDiscounts,
  };
};

export const getDiscountDetailsText = ({
  discountAppliesTo,
  discountValue,
  quantityLimit,
  valueType,
}: {
  discountAppliesTo: DiscountType;
  discountValue: string;
  quantityLimit?: number;
  valueType: string;
}) => {
  const formattedDiscountValue = formatDiscountValue(valueType, discountValue);

  const discountTypeText = getHumanizedDiscountType(discountAppliesTo);

  const quantityLimitText = quantityLimit
    ? `• Only apply to the first ${quantityLimit} units`
    : "";

  return `${formattedDiscountValue} off ${discountTypeText} ${quantityLimitText}`;
};

export const getDiscountTexts = (discount: Discount) => {
  const discountDetails = getDiscountDetailsText({
    discountAppliesTo: determineDiscountType(discount),
    discountValue: discount.value,
    quantityLimit: discount.quantityLimit,
    valueType: discount.discountType,
  });

  const services = discount.discountServiceMenuItems.map(
    (service) => service.serviceMenuItem.name
  );
  const serviceProducts = discount.discountServiceProducts.map(
    (serviceProduct) => serviceProduct.medspaServiceProduct.serviceProduct.name
  );

  const retailProducts = discount.discountRetailProducts.map(
    (retailProduct) => retailProduct.product.name
  );

  return {
    discountDetails,
    services,
    serviceProducts,
    retailProducts,
  };
};

type BaseDiscountLine = {
  id: string;
  isInvoiceLevel: boolean;
  discountType: string;
};

type DiscountTotalPrice = {
  id: string;
  discountTotal?: string | null;
};

export function useShapedDiscountLines<T extends BaseDiscountLine>(
  discounts: T[],
  discountsTotalPrices: DiscountTotalPrice[]
): (T & { discountTotal: string })[] {
  return useMemo(
    () =>
      discounts
        .map((discount) => ({
          ...discount,
          discountTotal:
            discountsTotalPrices.find((price) => price.id === discount.id)
              ?.discountTotal ?? "0.00",
        }))
        // Order by:
        // 1. Line Item Level discounts > Invoice Level discounts
        // 2. Fixed discount > Percent discount
        // 3. Discount Total High -> Low
        .sort((a, b) => {
          if (a.isInvoiceLevel == b.isInvoiceLevel) {
            if (a.discountType === b.discountType) {
              return +b.discountTotal - +a.discountTotal;
            }
            return a.discountType === DiscountTypeValues.PERCENT ? 1 : -1;
          }
          return a.isInvoiceLevel ? 1 : -1;
        }),
    [discounts, discountsTotalPrices]
  );
}
